import { Box, Grid, MenuItem, Select, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import OrcaScreenshot1 from "./images/orca_screenshot_1.png";
import OrcaScreenshot2 from "./images/orca_screenshot_2.png";
import OrcaScreenshot3 from "./images/orca_screenshot_3.png";
import OrcaScreenshot4 from "./images/orca_screenshot_4.png";
import OrcaScreenshot5 from "./images/orca_screenshot_5.png";
import OrcaScreenshot6 from "./images/orca_screenshot_6.png";
import OrcaConnectForm from "./OrcaConnectForm";

const OrcaIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("orca");
  const [loading, setLoading] = useState(false);
  const [regionUrl, setRegionUrl] = useState("");
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionCode, setRegionCode] = useState(null);
  const [regionError, setRegionError] = useState(null);
  const queryClient = useQueryClient();
  const SUPPORTED_REGIONS = {
    "US Global": { url: "https://app.orcasecurity.io/login", code: "" },
    Europe: { url: "https://eu.app.orcasecurity.io/login", code: "eu" },
    Australia: { url: "https://au.app.orcasecurity.io/login", code: "au" },
    India: { url: "https://in.app.orcasecurity.io/login", code: "in" },
    Israel: { url: "https://app.il.orcasecurity.io/login", code: "il" },
    "GOV Cloud": {
      url: "https://us.gov.app.orcasecurity.io/login",
      code: "us.gov",
    },
  };
  const APITokenNameExample = "Breeze Security Platform";

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerOrcaIntegration } = useAPI();

  const onSubmit = (values) => {
    if (!selectedRegion) {
      setRegionError("Please select a region before trying to connect.");
      Toast.error("Missing region.");
      return;
    }
    setRegionError(null);

    setLoading(true);
    registerOrcaIntegration(values.apiToken.trim(), regionCode.trim())
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Orca integrated successfully");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Orca and prioritize the suggested remediations."
            : ""
        }
        image={logos.OrcaLogo}
        iconBorderRadius={0}
        name={"Orca"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Orca is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Choose region and login to the Orca console">
                  <Typography variant="body1">
                    Choose the region that your Orca console is assigned to:
                  </Typography>
                  <Box
                    display="block"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Select
                      style={{
                        width: "180px",
                        height: "36px",
                        marginRight: "20px",
                      }}
                      value={
                        selectedRegion ? selectedRegion : "Select a region"
                      }
                      onChange={(event) => {
                        const regionName = event.target.value;
                        const regionData = SUPPORTED_REGIONS[regionName];
                        if (regionData) {
                          setRegionUrl(regionData.url);
                          setSelectedRegion(regionName);
                          setRegionCode(regionData.code);
                          setRegionError(null);
                        }
                      }}
                    >
                      <MenuItem value="Select a region">
                        Select a region
                      </MenuItem>
                      {Object.keys(SUPPORTED_REGIONS).map((regionName) => (
                        <MenuItem key={regionName} value={regionName}>
                          {regionName}
                        </MenuItem>
                      ))}
                    </Select>{" "}
                    {""}
                    <PrimaryButton
                      onClick={() => {
                        window.open(regionUrl, "_blank");
                      }}
                      disabled={!selectedRegion}
                    >
                      Login
                    </PrimaryButton>
                  </Box>
                  {regionError && (
                    <Typography color="error">{regionError}</Typography>
                  )}
                </GuidelineStep>
                <GuidelineStep title="Go to settings">
                  <Typography variant="body1">
                    Click on <strong>Settings</strong> on the left menu.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: OrcaScreenshot1,
                        altext: "Go to settings",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Go to API settings">
                  <Typography variant="body1">
                    Expand <strong>Users & Permissions</strong> and click on{" "}
                    <strong>API</strong>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: OrcaScreenshot2,
                        altext: "Go to API",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Add API token">
                  <Typography variant="body1">
                    1. Click on the <strong>Add API Token</strong>/
                    <strong>Create API Token</strong> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: OrcaScreenshot3,
                        altext: "Add API token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: OrcaScreenshot4,
                        altext: "Create API token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body1">
                      2. Name the new API token, for example, "
                      {APITokenNameExample}".
                    </Typography>

                    <CustomCopyButton
                      copyTitle={"name"}
                      copyText={APITokenNameExample}
                    />
                  </Box>
                  <Typography variant="body1">
                    3. Click on the <strong>Never Expire</strong> checkbox (Note
                    - If expiration date is set - When a token <br /> expires,
                    the integration needs to be deleted and re-integrated).
                  </Typography>
                  <Typography variant="body1">
                    4. Click on the <strong>Service Token</strong> checkbox
                    (Detach the token from the logged-in user).
                  </Typography>
                  <Typography variant="body1">
                    5. Select the <strong>Internal Viewer</strong> role and
                    click <strong>Add</strong>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: OrcaScreenshot5,
                        altext: "Add API token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Copy API token">
                  <Typography variant="body1">
                    Copy the generated API token value and paste in the{" "}
                    <a href="#apiToken">form below.</a>
                  </Typography>
                  <Typography variant="body1">
                    Then, click <strong>Continue</strong> to complete the
                    process.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: OrcaScreenshot6,
                        altext: "Copy API token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep
                  id="connectOrca"
                  title='Paste API token and Orca URL below and click "Connect"'
                >
                  <Box width={700}>
                    <OrcaConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default OrcaIntegration;
